import { removeFromArray } from '@standardnotes/utils';
import { ActionAccessType, ActionsExtensionMutator, MutationType, CreateDecryptedItemFromPayload, EncryptedPayload, isErrorDecryptingPayload, CreateEncryptedBackupFileContextPayload, } from '@standardnotes/models';
import { AbstractService, ChallengeValidation, ChallengeReason, ChallengePrompt, Challenge, } from '@standardnotes/services';
import { ContentType } from '@standardnotes/domain-core';
import { jtString, SupportEmail } from '@standardnotes/features';
import { c } from 'ttag';
/**
 * The Actions Service allows clients to interact with action-based extensions.
 * Action-based extensions are mostly RESTful actions that can push a local value or
 * retrieve a remote value and act on it accordingly.
 * There are 4 action types:
 * `get`: performs a GET request on an endpoint to retrieve an item value, and merges the
 *      value onto the local item value. For example, you can GET an item's older revision
 *      value and replace the current value with the revision.
 * `render`: performs a GET request, and displays the result in the UI. This action does not
 *         affect data unless action is taken explicitely in the UI after the data is presented.
 * `show`: opens the action's URL in a browser.
 * `post`: sends an item's data to a remote service. This is used for example by Listed
 *       to allow publishing a note to a user's blog.
 */
export class ActionsService extends AbstractService {
    constructor(itemManager, alertService, device, httpService, encryptionService, challengeService, internalEventBus) {
        super(internalEventBus);
        this.itemManager = itemManager;
        this.alertService = alertService;
        this.device = device;
        this.httpService = httpService;
        this.encryptionService = encryptionService;
        this.challengeService = challengeService;
        this.internalEventBus = internalEventBus;
        this.previousPasswords = [];
        this.payloadRequestHandlers = [];
        this.previousPasswords = [];
    }
    deinit() {
        ;
        this.itemManager = undefined;
        this.alertService = undefined;
        this.device = undefined;
        this.httpService = undefined;
        this.challengeService = undefined;
        this.encryptionService = undefined;
        this.payloadRequestHandlers.length = 0;
        this.previousPasswords.length = 0;
        super.deinit();
    }
    addPayloadRequestHandler(handler) {
        this.payloadRequestHandlers.push(handler);
        return () => {
            removeFromArray(this.payloadRequestHandlers, handler);
        };
    }
    getExtensions() {
        const extensionItems = this.itemManager.getItems(ContentType.TYPES.ActionsExtension);
        const excludingListed = extensionItems.filter((extension) => !extension.isListedExtension);
        return excludingListed;
    }
    extensionsInContextOfItem(item) {
        return this.getExtensions().filter((ext) => {
            return ext.supported_types.includes(item.content_type) || ext.actionsWithContextForItem(item).length > 0;
        });
    }
    /**
     * Loads an extension in the context of a certain item.
     * The server then has the chance to respond with actions that are
     * relevant just to this item. The response extension is not saved,
     * just displayed as a one-time thing.
     */
    async loadExtensionInContextOfItem(extension, item) {
        const params = {
            content_type: item.content_type,
            item_uuid: item.uuid,
        };
        const response = (await this.httpService.getAbsolute(extension.url, params).catch((response) => {
            console.error('Error loading extension', response);
            return undefined;
        }));
        if (!response) {
            return;
        }
        const description = response.description || extension.description;
        const supported_types = response.supported_types || extension.supported_types;
        const actions = (response.actions || []);
        const mutator = new ActionsExtensionMutator(extension, MutationType.UpdateUserTimestamps);
        mutator.deprecation = response.deprecation;
        mutator.description = description;
        mutator.supported_types = supported_types;
        mutator.actions = actions;
        const payloadResult = mutator.getResult();
        return CreateDecryptedItemFromPayload(payloadResult);
    }
    async runAction(action, item) {
        let result;
        switch (action.verb) {
            case 'render':
                result = await this.handleRenderAction(action);
                break;
            case 'show':
                result = this.handleShowAction(action);
                break;
            case 'post':
                result = await this.handlePostAction(action, item);
                break;
            default:
                break;
        }
        return result;
    }
    async handleRenderAction(action) {
        const response = await this.httpService
            .getAbsolute(action.url)
            .then(async (response) => {
            const payload = await this.payloadByDecryptingResponse(response);
            if (payload) {
                const item = CreateDecryptedItemFromPayload(payload);
                return {
                    ...response,
                    item,
                };
            }
            else {
                return undefined;
            }
        })
            .catch((response) => {
            const error = (response && response.error) || {
                message: c('B2.NavSharedUI.Error').t `An issue occurred while processing this action. Please try again.`,
            };
            void this.alertService.alert(error.message);
            return { error };
        });
        return response;
    }
    async payloadByDecryptingResponse(response, rootKey, triedPasswords = []) {
        if (!response.item || response.item.deleted || response.item.content == undefined) {
            return undefined;
        }
        const payload = new EncryptedPayload(response.item);
        if (!payload.enc_item_key) {
            void this.alertService.alert(c('B4.Notes.History.Error').t `This revision is missing its key and cannot be recovered.`);
            return;
        }
        let decryptedPayload = await this.encryptionService.decryptSplitSingle({
            usesItemsKeyWithKeyLookup: {
                items: [payload],
            },
        });
        if (!isErrorDecryptingPayload(decryptedPayload)) {
            return decryptedPayload;
        }
        if (rootKey) {
            decryptedPayload = await this.encryptionService.decryptSplitSingle({
                usesRootKey: {
                    items: [payload],
                    key: rootKey,
                },
            });
            if (!isErrorDecryptingPayload(decryptedPayload)) {
                return decryptedPayload;
            }
        }
        for (const itemsKey of this.itemManager.getDisplayableItemsKeys()) {
            const decryptedPayload = await this.encryptionService.decryptSplitSingle({
                usesItemsKey: {
                    items: [payload],
                    key: itemsKey,
                },
            });
            if (!isErrorDecryptingPayload(decryptedPayload)) {
                return decryptedPayload;
            }
        }
        const keyParamsData = response.keyParams || response.auth_params;
        if (!keyParamsData) {
            /**
             * In some cases revisions were missing auth params.
             * Instruct the user to email us to get this remedied.
             */
            void this.alertService.alert(jtString(c('B4.Notes.History.Error')
                .jt `We were unable to decrypt this revision using your current keys, and this revision is missing metadata that would allow us to try different keys to decrypt it. This can likely be fixed with some manual intervention. Please email ${SupportEmail} for assistance.`));
            return undefined;
        }
        const keyParams = this.encryptionService.createKeyParams(keyParamsData);
        /* Try previous passwords */
        for (const passwordCandidate of this.previousPasswords) {
            if (triedPasswords.includes(passwordCandidate)) {
                continue;
            }
            triedPasswords.push(passwordCandidate);
            const key = await this.encryptionService.computeRootKey(passwordCandidate, keyParams);
            if (!key) {
                continue;
            }
            const nestedResponse = await this.payloadByDecryptingResponse(response, key, triedPasswords);
            if (nestedResponse) {
                return nestedResponse;
            }
        }
        /** Prompt for other passwords */
        const password = await this.promptForLegacyPassword();
        if (!password) {
            return undefined;
        }
        if (this.previousPasswords.includes(password)) {
            return undefined;
        }
        this.previousPasswords.push(password);
        return this.payloadByDecryptingResponse(response, rootKey);
    }
    async promptForLegacyPassword() {
        const challenge = new Challenge([
            new ChallengePrompt(ChallengeValidation.None, c('B5.SecuritySync.Challenge.Label').t `Previous Password`, undefined, true),
        ], ChallengeReason.Custom, true, c('B4.Notes.History.Info')
            .t `Unable to find key for revision. Please enter the account password you may have used at the time of the revision.`);
        const response = await this.challengeService.promptForChallengeResponse(challenge);
        return response === null || response === void 0 ? void 0 : response.getDefaultValue().value;
    }
    async handlePostAction(action, item) {
        const decrypted = action.access_type === ActionAccessType.Decrypted;
        const itemParams = await this.outgoingPayloadForItem(item, decrypted);
        const params = {
            items: [itemParams],
        };
        return this.httpService
            .postAbsolute(action.url, params)
            .then((response) => {
            return response;
        })
            .catch((response) => {
            console.error('Action error response:', response);
            void this.alertService.alert(c('B2.NavSharedUI.Error').t `An issue occurred while processing this action. Please try again.`);
            return response;
        });
    }
    handleShowAction(action) {
        void this.device.openUrl(action.url);
        return {};
    }
    async outgoingPayloadForItem(item, decrypted = false) {
        const payloadFromHandler = this.getPayloadFromRequestHandlers(item.uuid);
        if (payloadFromHandler) {
            return payloadFromHandler;
        }
        if (decrypted) {
            return item.payload.ejected();
        }
        const encrypted = await this.encryptionService.encryptSplitSingle({
            usesItemsKeyWithKeyLookup: { items: [item.payload] },
        });
        return CreateEncryptedBackupFileContextPayload(encrypted);
    }
    getPayloadFromRequestHandlers(uuid) {
        for (const handler of this.payloadRequestHandlers) {
            const payload = handler(uuid);
            if (payload) {
                return payload;
            }
        }
        return undefined;
    }
}
